<template>
    <div>
        <div class="payment-select">
            <PageTitleBanner
                :title="$t('m.paymentTitle')"
                showIcon
                :showBack="mapEnabled"
                backIcon="map"
                :back="mapRoute"
            />
            <PaymentSelectSkeleton v-if="isLoading" />
            <template v-else>
                <OwnedSubscriptions
                    v-if="showOwnedSubscriptions"
                    :billingRule="billingRule"
                    :ownedSubscriptionList="ownedSubscriptionList"
                    :authorizationRequired="ownedSubscriptionRequiresPayment"
                />
                <BillingRuleDisplay
                    v-if="showBillingRuleDisplay"
                    :billingRule="billingRule"
                    :defaultSubscriptionDetail="defaultSubscriptionDetail"
                />
                <SubscriptionList
                    v-if="showSubscriptionList"
                    :billingRule="billingRule"
                    :subscriptionList="subscriptionList"
                    :defaultSubscriptionDetail="defaultSubscriptionDetail"
                    :defaultSelected="defaultSelected"
                    @switchToDefault="switchToDefault"
                    @switchSubscription="switchSubscription"
                    @switchSubscriptionAutoRenew="switchSubscriptionAutoRenew"
                />
                <PaymentMethods
                    v-if="showPaymentMethods"
                    v-bind="paymentMethodsViewProps"
                    :authorizationRequired="ownedSubscriptionRequiresPayment"
                    @update:selectedMethod="selectedMethod = $event"
                    @update:selectedCardId="selectedCardId = $event"
                    @refresh-wallet="getUserWallet"
                    @show-recharge="handleShowRecharge"
                    @show-loading="handleShowLoading"
                    @hide-loading="handleHideLoading"
                    @station-busy-change="handleStationBusyChange"
                    @toast="toast($event)"
                    @guest-required="handleGuestRequired"
                    @oneclick-add="handleOneclickAdd"
                    @thawani-pay="handleThawaniPay"
                    @subscription-success="handleSubscriptionSuccess"
                />
                <div v-if="showGuestAgreement" class="privacy-agreement-spacer"></div>
                <div
                    v-if="showGuestAgreement"
                    :class="['privacy-agreement', { shake: showShakeEffect }]"
                >
                    <input
                        id="payment-privacy-checkbox"
                        v-model="agreePolicy"
                        type="checkbox"
                        name="agreePolicy"
                        class="privacy-checkbox"
                    />
                    <label
                        for="payment-privacy-checkbox"
                        class="privacy-text"
                        v-html="replaceTranslateContent($t('m.policyAndTerms'))"
                    ></label>
                </div>
                <PaymentButton
                    v-show="showUnlockNow"
                    @click="submitReleaseDirectly"
                >
                    <span>{{ $t("m.unlockNow") }}</span>
                </PaymentButton>
                <PaymentButton
                    v-show="showCardConfirmButton"
                    @click="submitCardPayment"
                >
                    <span>{{ $t("m.confirm") }}</span>
                </PaymentButton>
                <PaymentButton
                    v-show="showBalanceConfirmButton"
                    @click="submitBalancePayment"
                >
                    <span>{{ $t("m.confirm") }}</span>
                </PaymentButton>
                <template v-if="showThirdPartyPayButtons">
                    <UnifiedPayButton
                        v-for="method in paymentMethods"
                        :ref="`unifiedPayButton-${method.method}`"
                        v-show="shouldShowUnifiedPayButton(method)"
                        :key="`${method.gateway}-${method.method}`"
                        :availability-resolved="method.isAvailabilityResolved"
                        :availability-available="method.isAvailable"
                        :payment-gateway="method.gateway"
                        :selected-method="method.method"
                        :billing-rule="billingRule"
                        :selected-subscription="selectedSubscription"
                        :authorization-subscription-id="authorizationSubscription?.id"
                        :config="paymentConfig"
                        :device-code="deviceCode"
                        @update-available="updatePaymentAvailable"
                        @after-payment="handlePaymentResult"
                        @toast="toast($event)"
                    />
                </template>
                <PopupConfirm
                    :show-header="false"
                    :showPopup="isShowRepaymentPopup"
                >
                    <div class="unpaid-popup">
                        <div class="icon">
                            <WarningIcon />
                        </div>
                        <div class="subtitle">
                            {{ $t("m.unpaidPopupSubtitle") }}
                        </div>
                        <div class="content">
                            {{ $t("m.unpaidPopupContent") }}
                        </div>
                        <span
                            v-active-touch
                            class="primary-btn"
                            @click="$router.push({ path: '/repayment_select' })"
                        >
                            {{ $t("m.payNow") }}
                        </span>
                    </div>
                </PopupConfirm>
                <PopupConfirm :show-header="false" :showPopup="isShowGuestPopup" @hidePopup="isShowGuestPopup = false">
                    <div class="unpaid-popup guest-popup">
                        <div class="icon">
                            <WarningIcon />
                        </div>
                        <div class="subtitle">
                            {{ $t("m.guestLoginTips") }}
                        </div>
                        <span
                            v-active-touch
                            class="primary-btn"
                            @click="goLogin"
                        >
                            {{ $t("m.confirm") }}
                        </span>
                    </div>
                </PopupConfirm>
                <RechargePopup
                    :visible.sync="isShowRechargePopup"
                    v-bind="rechargePopupProps"
                    @refresh-wallet="getUserWallet"
                    @show-loading="handleShowLoading"
                    @hide-loading="handleHideLoading"
                    @toast="toast($event)"
                    @oneclick-add="handleOneclickAdd"
                    @thawani-pay="handleThawaniPay"
                    @recharge-success="handleRechargeSuccess"
                    @show-iframe="handleShowIframe"
                    @set-internal-id="setInternalId"
                />
                <PaymentIframePopup
                    :visible.sync="isShowPaymentPopup"
                    :htmlContent="iframeHtmlContent"
                    @close="closePaymentPopup"
                />
            </template>
            <PreparingCharger
                v-if="isShowPreparing"
                :subtitle="preparingSubtitle"
            ></PreparingCharger>
        </div>
        <SplashScreen v-if="isShowSplash"></SplashScreen>
    </div>
</template>
<script>
import {
    getUnpaidRent,
    getPaymentConfig,
    getUserWallet,
    getUserInfo,
    getSubscriptions,
    getBillingRule,
    confirmBalancePayment,
} from "../../api/logined";
import PageTitleBanner from "../../components/PageTitleBanner.vue";
import PaymentButton from "./components/PaymentButton.vue";
import UnifiedPayButton from "./components/UnifiedPayButton.vue";
import PopupConfirm from "../../components/popups/PopupConfirm.vue";
import PreparingCharger from "../../components/loading/PreparingCharger.vue";
import SplashScreen from "../../components/loading/SplashScreen.vue";
import WarningIcon from "../../components/icons/WarningIcon.vue";
import OwnedSubscriptions from "./components/OwnedSubscriptions.vue";
import BillingRuleDisplay from "./components/BillingRuleDisplay.vue";
import SubscriptionList from "./components/SubscriptionList.vue";
import PaymentMethods from "./components/PaymentMethods.vue";
import PaymentSelectSkeleton from "./components/PaymentSelectSkeleton.vue";
import RechargePopup from "./components/RechargePopup.vue";
import PaymentIframePopup from "./components/PaymentIframePopup.vue";
import paymentProcessingMixin from "./mixins/paymentProcessing";
import { paymentService } from "@/services/paymentService";
import { getMapRouteOrFallback } from "@/utils/mapFeature";

const MACHINE_CONFIG_ERROR_CODES = [50101, 50102, 50106, 50107, 50108, 50110];

export default {
    name: "PaymentSelect",
    mixins: [paymentProcessingMixin],
    components: {
        PageTitleBanner,
        PaymentButton,
        UnifiedPayButton,
        PopupConfirm,
        PreparingCharger,
        SplashScreen,
        WarningIcon,
        OwnedSubscriptions,
        BillingRuleDisplay,
        SubscriptionList,
        PaymentMethods,
        PaymentSelectSkeleton,
        RechargePopup,
        PaymentIframePopup,
    },
    data() {
        return {
            isLoading: true,
            billingRule: undefined,
            stripe: undefined,
            card: undefined,
            deviceCode: undefined,
            applePayAvailable: false,
            googlePayAvailable: false,
            selectedMethod: undefined,
            selectedCardId: undefined,
            isShowRepaymentPopup: false,
            paymentConfigList: [],
            bindCardValid: false,
            isShowPreparing: false,
            fortisCreateOrderRes: {},
            isShowSplash: false,
            defaultSubscriptionDetail: "",
            subscriptionList: undefined,
            defaultSelected: false,
            ownedSubscriptionList: undefined,
            outletId: undefined,
            paymentMethods: [],
            cardList: [],
            userBalance: 0,
            userMinimalBalance: 0,
            isShowRechargePopup: false,
            isShowPaymentPopup: false,
            rechargeAmounts: [],
            iframeHtmlContent: "",
            highlightPaymentMethodTimer: null,
            agreePolicy: true,
            showShakeEffect: false,
            isShowGuestPopup: false,
        };
    },
    async created() {
        if (this.$route.query.toast_msg) {
            this.toast(this.$route.query.toast_msg);
            const query = { ...this.$route.query };
            delete query.toast_msg;
            this.$router.replace({ query }).catch(() => {});
        }
        this.isLoading = true;
        this.deviceCode = this.$store.getters["appConfig/device_code"];
        this.outletId = this.$store.getters["appConfig/outlet_id"];
        if (!this.deviceCode) {
            this.isLoading = false;
            this.$router.replace(this.mapRoute).catch(() => {});
            return;
        }

        try {
            const isUnpaid = await this.getUnpaidRent();
            if (isUnpaid) {
                this.isLoading = false;
                return;
            }
            await Promise.all([
                this.getUserWallet(),
                this.getPaymentConfig(),
                this.getSubscriptions(),
                this.getBillingRule(),
            ]);

            this.handleHighlightCardAfterBind();
            this.handleHighlightPaymentMethodAfterReturn();
        } catch (error) {
            this.handleInitError(error);
        } finally {
            this.isLoading = false;
        }
    },
    async mounted() {
        if (window.location.hostname === "qr.heycharge.global") {
            document.addEventListener(
                "visibilitychange",
                this.handleVisibilityChange
            );
        }
        this.rechargeAmounts =
            this.$store.getters["appConfig/recharge_amount_gradient"] || [];
    },
    beforeDestroy() {
        if (window.location.hostname === "qr.heycharge.global") {
            document.removeEventListener(
                "visibilitychange",
                this.handleVisibilityChange
            );
        }
        if (this.highlightPaymentMethodTimer) {
            clearTimeout(this.highlightPaymentMethodTimer);
            this.highlightPaymentMethodTimer = null;
        }
    },
    computed: {
        mapEnabled() {
            return this.$store.getters["appConfig/map_enabled"];
        },
        preparingSubtitle() {
            return this.borrowStationBusyWaiting
                ? this.$t("m.preparingStationBusySubtitle")
                : "";
        },
        subscriptionListLength() {
            return this.subscriptionList?.length ?? 0;
        },
        hasOwnedSubscriptions() {
            return (this.ownedSubscriptionList?.length ?? 0) > 0;
        },
        isOrdinaryRentMode() {
            return (
                (this.ownedSubscriptionList?.length === 0 ||
                    this.allSubscriptionsExpired) &&
                !this.selectedSubscription?.id
            );
        },
        showOwnedSubscriptions() {
            return !!this.billingRule && this.hasOwnedSubscriptions;
        },
        showBillingRuleDisplay() {
            return (
                !!this.billingRule &&
                this.subscriptionListLength === 0 &&
                (this.ownedSubscriptionList?.length === 0 ||
                    this.allSubscriptionsExpired ||
                    this.ownedSubscriptionRequiresPayment)
            );
        },
        showSubscriptionList() {
            return !!this.billingRule && this.subscriptionListLength > 0;
        },
        showPaymentMethods() {
            const hasPurchasableSubscriptions =
                !this.hasOwnedSubscriptions && this.subscriptionListLength > 0;
            return (
                !!this.billingRule &&
                !this.isFreeBorrow &&
                (this.isOrdinaryRentMode ||
                    hasPurchasableSubscriptions ||
                    this.ownedSubscriptionRequiresPayment)
            );
        },
        showUnlockNow() {
            return (
                this.isFreeBorrow ||
                this.canUnlockWithOwnedSubscriptionDirectly
            );
        },
        showCardConfirmButton() {
            return (
                !this.isFreeBorrow &&
                this.selectedMethod === "card" &&
                !!this.selectedCardId
            );
        },
        showBalanceConfirmButton() {
            return (
                this.hasThawani &&
                !this.isFreeBorrow &&
                this.selectedMethod === "balance" &&
                !this.rechargeMode
            );
        },
        showThirdPartyPayButtons() {
            return !!this.billingRule && !this.isFreeBorrow;
        },
        paymentConfigMap() {
            return paymentService.getGatewayConfig(this.paymentConfigList);
        },
        paymentConfig() {
            return {
                stripe: this.stripe,
                fortisGatewayId: this.paymentConfigMap.fortis?.gatewayId || 0,
                merchantName: this.paymentConfigMap.fortis?.merchant_name || "",
                authJwt: this.paymentConfigMap.fortis?.googlejwt || "",
                merchantID: this.paymentConfigMap.fortis?.merchant_id || "",
                merchantGatewayID:
                    this.paymentConfigMap.fortis?.wallet_merchant_id || "",
                oneclickGatewayId:
                    this.paymentConfigMap.oneclick?.gatewayId || 0,
                thawaniGatewayId: this.paymentConfigMap.thawani?.gatewayId || 0,
                moyasarGatewayId: this.paymentConfigMap.moyasar?.gatewayId || 0,
                moyasar: this.paymentConfigMap.moyasar || {},
                brandName: this.$store.getters["appConfig/brand_name"] || "",
                countryCode: this.$store.getters["appConfig/country_code"] || "",
            };
        },
        hasThawani() {
            return !!this.paymentConfigMap.thawani?.gatewayId;
        },
        autoQuickLogin() {
            return this.$store.getters["appConfig/auto_quick_login"];
        },
        showGuestAgreement() {
            return this.autoQuickLogin && !!this.billingRule?.currency;
        },
        stripeGatewayId() {
            return this.paymentConfigMap.stripe?.gatewayId || 0;
        },
        fortisGatewayId() {
            return this.paymentConfigMap.fortis?.gatewayId || 0;
        },
        applePayGatewayId() {
            // 查找支持 apple_pay 或 web_apple_pay 的网关
            return this._getGatewayId("apple_pay", "web_apple_pay");
        },
        googlePayGatewayId() {
            // 查找支持 google_pay 或 web_google_pay 的网关
            return this._getGatewayId("google_pay", "web_google_pay");
        },
        cardGatewayId() {
            // 查找支持 card 或 web_card 的网关
            return this._getGatewayId("card", "web_card");
        },
        selectedSubscription() {
            const purchasableSubscription =
                this.subscriptionList?.find((item) => item.checked) || {};
            return purchasableSubscription.id ? purchasableSubscription : {};
        },
        isSubscriptionPurchaseFlow() {
            return !!this.selectedSubscription?.id;
        },
        allSubscriptionsExpired() {
            return this.ownedSubscriptionList?.every(
                (item) =>
                    item.available_subscription === 0 ||
                    (item.available_subscription === undefined &&
                        item.subscription_status !== 1)
            );
        },
        anySubscriptionActive() {
            return this.ownedSubscriptionList?.some(
                (item) =>
                    item.available_subscription === 1 ||
                    (item.available_subscription === undefined &&
                        item.subscription_status === 1)
            );
        },
        authorizationSubscription() {
            return (
                this.ownedSubscriptionList?.find(
                    (item) =>
                        item.available_subscription === 1 ||
                        (item.available_subscription === undefined &&
                            item.subscription_status === 1)
                ) || {}
            );
        },
        canUnlockWithOwnedSubscriptionDirectly() {
            if (!this.billingRule) return false;
            if (!this.hasOwnedSubscriptions || !this.anySubscriptionActive) return false;
            return (
                Number(this.billingRule.no_need_payment_method) === 1 &&
                Number(this.userMinimalBalance ?? 0) === 0
            );
        },
        ownedSubscriptionRequiresPayment() {
            return (
                this.hasOwnedSubscriptions &&
                this.anySubscriptionActive &&
                !this.canUnlockWithOwnedSubscriptionDirectly
            );
        },
        isFreeBorrow() {
            if (!this.billingRule) return false;

            const noNeedPaymentMethod = this.billingRule.no_need_payment_method;

            const lowlimitBalance = this.billingRule.low_limit_balance;

            return (
                this.isOrdinaryRentMode &&
                Number(lowlimitBalance) === 0 &&
                Number(noNeedPaymentMethod) === 1
            );
        },

        balanceThreshold() {
            const rule = this.billingRule || {};
            const noNeedPaymentMethod = Number(rule.no_need_payment_method ?? 1);
            if (noNeedPaymentMethod === 0) {
                return Number(rule.deposit ?? 0);
            }
            return Number(rule.low_limit_balance ?? rule.low_limit ?? 0);
        },
        rechargeMode() {
            if (!this.hasThawani) return false;
            const threshold = this.balanceThreshold;
            const balance = Number(this.userBalance ?? 0);
            return (
                Number.isFinite(threshold) &&
                threshold > 0 &&
                balance < threshold
            );
        },
        isTopUpMethodSelected() {
            if (!this.topUpSelectedMethod) return false;
            if (
                this.topUpSelectedMethod === "card" ||
                this.topUpSelectedMethod === "web_card"
            ) {
                return !!this.topUpSelectedCardId;
            }
            return true;
        },
        paymentMethodsViewProps() {
            return {
                showBalance: this.hasThawani,
                userBalance: this.userBalance,
                rechargeMode: this.rechargeMode,
                paymentMethods: this.paymentMethods,
                cardList: this.cardList,
                selectedMethod: this.selectedMethod,
                selectedCardId: this.selectedCardId,
                cardGatewayId: this.cardGatewayId,
                stripeGatewayId: this.stripeGatewayId,
                fortisGatewayId: this.fortisGatewayId,
                stripe: this.stripe,
                paymentConfig: this.paymentConfig,
                selectedSubscription: this.selectedSubscription,
                authorizationSubscriptionId: this.authorizationSubscription?.id,
                deviceCode: this.deviceCode,
                outletId: this.outletId,
                beforeBindCard: () => this.ensureAutoQuickLoginAgreement(),
                createPaymentOrder: this.createPaymentOrder,
                doSubscriptionPayment: this.doSubscriptionPayment,
            };
        },
        rechargePopupProps() {
            return {
                balance: this.userBalance,
                balanceThreshold: this.balanceThreshold,
                rechargeAmounts: this.rechargeAmounts,
                paymentMethods: this.paymentMethods,
                cardList: this.cardList,
                cardGatewayId: this.cardGatewayId,
                stripeGatewayId: this.stripeGatewayId,
                fortisGatewayId: this.fortisGatewayId,
                oneclickGatewayId: this.paymentConfigMap.oneclick?.gatewayId || 0,
                thawaniGatewayId: this.paymentConfigMap.thawani?.gatewayId || 0,
                stripe: this.stripe,
                selectedSubscription: this.selectedSubscription,
                deviceCode: this.deviceCode,
                outletId: this.outletId,
            };
        },
        mapRoute() {
            return getMapRouteOrFallback({
                mapEnabled: this.mapEnabled,
                isBorrow: this.$store.getters.is_borrow,
                deviceCode: this.$store.getters["appConfig/device_code"],
                machinePrecheckError: this.$store.getters["appConfig/machine_precheck_error"],
            });
        },
    },
    watch: {
        hasThawani: {
            handler(v) {
                if (!v) {
                    if (this.selectedMethod === "balance") {
                        this.selectedMethod = undefined;
                    }
                    this.isShowRechargePopup = false;
                    this.isShowPaymentPopup = false;
                    this.topUpSelectedMethod = undefined;
                    this.topUpSelectedCardId = undefined;
                    this.userBalance = 0;
                }
            },
            immediate: true,
        },
        rechargeMode: {
            handler(v) {
                if (v && this.selectedMethod === "balance") {
                    this.selectedMethod = undefined;
                }
            },
            immediate: true,
        },
    },
    methods: {
        async handleVisibilityChange() {
            if (document.visibilityState === "hidden") {
                this.isShowSplash = true;
            } else {
                await new Promise((resolve) => setTimeout(resolve, 500));
                try {
                    await Promise.all([
                        this.getBillingRule(),
                        this.getPaymentConfig(),
                    ]);
                } catch (e) {}
                this.isShowSplash = false;
            }
        },
        handleInitError(error) {
            const errorCode = Number(error && error.code);
            if (MACHINE_CONFIG_ERROR_CODES.includes(errorCode)) {
                const deviceCode = this.deviceCode || this.$store.getters["appConfig/device_code"];
                const precheckError = {
                    code: errorCode,
                    message: error && error.message,
                    device_code: deviceCode,
                };
                this.$store.commit("appConfig/SET_MACHINE_PRECHECK_ERROR", precheckError);
                this.$router.replace(this.mapRoute).catch(() => {});
                return;
            }

            this.toast((error && error.message) || this.$t("m.serverError"));
        },
        async getUnpaidRent() {
            const res = await getUnpaidRent();
            if (res.data.have_unpaid) {
                this.isShowRepaymentPopup = true;
                this.$store.dispatch("setRepaymentInfo", res.data);
                return true;
            }
            this.$store.dispatch("setRepaymentInfo", {});
            return false;
        },
        async getUserWallet() {
            const cardRes = await getUserWallet({ exclude_no_reusable: 1 });
            this.cardList = cardRes.data?.list || [];
        },
        async getUserBalance() {
            if (!this.hasThawani) {
                this.userBalance = 0;
                this.userMinimalBalance = 0;
                return;
            }
            const res = await getUserInfo();
            this.userBalance = res?.data?.amount || 0;
            this.userMinimalBalance =
                res?.data?.minimal_balance ?? res?.data?.minimalBalance ?? 0;
        },
        _getGatewayId(stripeMethod, fortisMethod) {
            const stripeTypes = this.paymentConfigMap.stripe?.paymentMethodTypes || [];
            const fortisTypes = this.paymentConfigMap.fortis?.paymentMethodTypes || [];

            if (stripeTypes.includes(stripeMethod)) {
                return this.paymentConfigMap.stripe.gatewayId;
            }
            if (fortisTypes.includes(fortisMethod)) {
                return this.paymentConfigMap.fortis.gatewayId;
            }
            return 0;
        },
        _formatPricePeriod(period) {
            const selectPlural = (label, count) => {
                if (typeof label === "string" && label.includes("|")) {
                    const [singular, plural] = label.split("|").map((s) => s.trim());
                    return count === 1 ? singular : plural;
                }
                return label;
            };

            if (period === 30) {
                return selectPlural(this.$t("time.month"), 1);
            } else if (period === 365) {
                return selectPlural(this.$t("time.year"), 1);
            } else {
                return `${period} ${selectPlural(this.$t("time.day"), period)}`;
            }
        },
        async getSubscriptions() {
            try {
                const res = await getSubscriptions({
                    outlet_id: this.outletId,
                });

                if (res.data.owned_list?.length > 0) {
                    this.ownedSubscriptionList = res.data.owned_list;
                    this.subscriptionList = [];
                    return;
                }

                this.ownedSubscriptionList = [];
                this.subscriptionList = (res.data.list || []).map((item, index) => ({
                    ...item,
                    checked: index === 0,
                    auto_renew: false,
                    price_period: this._formatPricePeriod(parseInt(item.period)),
                }));

            } catch (er) {
                this.toast(er && er.message);
                throw er;
            }
        },
        getUnifiedPayButtonRef(method) {
            const ref = this.$refs[`unifiedPayButton-${method}`];
            const button = Array.isArray(ref) ? ref[0] : ref;
            return button;
        },
        async triggerUnifiedPayButton(method) {
            const button = this.getUnifiedPayButtonRef(method);
            if (button && typeof button.handlePayment === "function") {
                await button.handlePayment();
            }
        },
        async handleOneclickAdd() {
            await this.triggerUnifiedPayButton("oneclick");
        },
        async handleThawaniPay() {
            await this.triggerUnifiedPayButton("thawani");
        },
        shouldShowUnifiedPayButton(method) {
            return (
                !!method &&
                method.isAvailable &&
                this.selectedMethod === method.method &&
                !(method.gateway === "moyasar" && method.method === "applepay") &&
                method.method !== "card" &&
                method.method !== "web_card" &&
                method.method !== "oneclick" &&
                method.method !== "thawani"
            );
        },
        shouldUseGlobalLoading(meta) {
            const scope =
                meta &&
                (meta.loadingScope ||
                    meta.loading_scope ||
                    meta.loading ||
                    meta.loading_type);
            if (scope === "global") return true;
            if (scope === "local") return false;

            const predictableRedirectGateways = ["thawani", "moyasar"];
            const gateway = meta && (meta.gateway || meta.paymentGateway);
            return !!(gateway && predictableRedirectGateways.includes(gateway));
        },
        handleShowLoading(meta) {
            if (this.shouldUseGlobalLoading(meta)) {
                this.showLoading();
            } else {
                this.isShowPreparing = true;
            }
        },
        handleHideLoading(meta) {
            this.borrowStationBusyWaiting = false;
            if (this.shouldUseGlobalLoading(meta)) {
                this.hideLoading();
            } else {
                this.isShowPreparing = false;
            }
        },
        handleStationBusyChange(waiting) {
            this.borrowStationBusyWaiting = !!waiting;
        },
        async getPaymentConfig() {
            const res = await getPaymentConfig({ code: this.deviceCode });
            this.paymentConfigList = res.data?.config_list || [];
            this.stripe = undefined;

            if (this.stripeGatewayId) {
                try {
                    await this.$loadScript("https://js.stripe.com/v3/");
                    const stripeOptions = {
                        locale: this.$i18n.locale,
                    };
                    const stripeAccount =
                        this.paymentConfigMap.stripe.connect_account_id;
                    if (stripeAccount) {
                        stripeOptions.stripeAccount = stripeAccount;
                    }
                    this.stripe = Stripe(this.paymentConfigMap.stripe.public_key, stripeOptions);
                } catch (error) {
                    console.error("Stripe init failed:", error);
                }
            }
            this.paymentMethods = paymentService.applyEarlyAvailabilityHints(
                paymentService.generatePaymentMethods(this.paymentConfigList)
            );
            if (this.hasThawani) {
                await this.getUserBalance();
            } else {
                this.userBalance = 0;
            }
        },
        getBillingRule() {
            return getBillingRule({ code: this.deviceCode }).then((res) => {
                this.$store.dispatch("setBillingRule", res.data);
                this.billingRule = this.$store.getters.billing_rule;
                this.defaultSubscriptionDetail =
                    this.billingRule.custom_price_info;
            });
        },
        async handleRechargeSuccess(internalId) {
            try {
                if (!internalId) {
                    this.toast(this.$t("tMsg.paymentFailed"));
                    return;
                }
                this.isShowPreparing = true;
                await confirmBalancePayment({
                    internal_id: internalId,
                    request_from: 1,
                });
                await this.getUserBalance();
                this.triggerHighlightBalance();
                this.isShowRechargePopup = false;
            } catch (error) {
                this.toastPaymentError(error);
                this.isShowRechargePopup = false;
            }
            this.isShowPreparing = false;
        },
        handleShowIframe(htmlContent) {
            this.iframeHtmlContent = htmlContent;
            this.isShowPaymentPopup = true;
        },
        setInternalId(id) {
            this.internalId = id;
            this.$store.commit("appConfig/SET_INTERNAL_ID", id);
        },
        async submitBalancePayment() {
            if (!this.hasThawani) {
                return;
            }
            if (!this.ensureAutoQuickLoginAgreement()) {
                return;
            }
            try {
                this.isShowPreparing = true;
                await this.borrow({
                    code: this.deviceCode,
                    subscriptionId: this.getBorrowSubscriptionId(),
                });
                this.$router.push({ name: "borrow_success" });
            } catch (error) {
                this.toastPaymentError(error);
            } finally {
                this.isShowPreparing = false;
            }
        },
        handleShowRecharge() {
            if (!this.hasThawani) {
                return;
            }
            this.isShowRechargePopup = true;
        },
        closePaymentPopup() {
            this.isShowPaymentPopup = false;
            this.iframeHtmlContent = "";
        },
        handleHighlightCardAfterBind() {
            const highlightId = this.$store.getters.highlight_card_id;
            if (!highlightId) {
                return;
            }
            const idNum = Number(highlightId);
            if (
                this.cardList &&
                this.cardList.some((card) => Number(card.id) === idNum)
            ) {
                // Clear highlight status after animation
                setTimeout(() => {
                    this.$store.commit("SET_HIGHLIGHT_CARD_ID", undefined);
                }, 3000);
            }
        },
        handleHighlightPaymentMethodAfterReturn() {
            const method = this.$store.getters.highlight_payment_method;
            if (!method) return;
            if (this.highlightPaymentMethodTimer) {
                clearTimeout(this.highlightPaymentMethodTimer);
                this.highlightPaymentMethodTimer = null;
            }
            this.highlightPaymentMethodTimer = setTimeout(() => {
                this.$store.commit("SET_HIGHLIGHT_PAYMENT_METHOD", undefined);
                this.highlightPaymentMethodTimer = null;
            }, 3000);
        },
        triggerHighlightBalance() {
            if (this.highlightPaymentMethodTimer) {
                clearTimeout(this.highlightPaymentMethodTimer);
                this.highlightPaymentMethodTimer = null;
            }
            this.$store.commit("SET_HIGHLIGHT_PAYMENT_METHOD", "balance");
            this.highlightPaymentMethodTimer = setTimeout(() => {
                this.$store.commit("SET_HIGHLIGHT_PAYMENT_METHOD", undefined);
                this.highlightPaymentMethodTimer = null;
            }, 3000);
        },
        resetFortisCard() {
            if (!this.card) return;

            if (this.$refs.bindCardFortis) {
                this.$refs.bindCardFortis.style.display = "none";
            }
            this.card.off("done");
            this.card.off("submitted");
            this.card.off("ready");
            this.card.off("error");
            this.card = undefined;

            document
                .querySelectorAll('[id^="bind-card-fortis-"]')
                .forEach((container) => {
                    container.innerHTML = "";
                });
        },
        switchToDefault() {
            this.resetFortisCard();
            (this.subscriptionList || []).forEach((subscription) => {
                subscription.checked = false;
                subscription.auto_renew = false;
            });
            this.defaultSelected = true;
        },
        switchSubscription(item) {
            if (item.checked) return;
            this.resetFortisCard();
            item.checked = true;
            this.defaultSelected = false;
            (this.subscriptionList || []).forEach((subscription) => {
                if (subscription.id !== item.id) {
                    subscription.checked = false;
                    subscription.auto_renew = false;
                }
            });
        },
        switchSubscriptionAutoRenew(item, e) {
            if (item.checked) {
                e.stopPropagation();
            }
        },
        updatePaymentAvailable(method, isAvailable, meta = {}) {
            const paymentMethod = this.paymentMethods.find(
                (m) => m.method === method
            );
            if (paymentMethod) {
                paymentMethod.isAvailable = isAvailable;
                paymentMethod.isAvailabilityChecking = !!meta.checking;
                paymentMethod.isAvailabilityResolved = !!meta.resolved;
            }
        },
        handlePaymentResult(data) {
            if (!data) return;
            const { provider, method, ev, paymentToken } = data;
            if (provider === "fortis" || paymentToken) {
                this.fortisThirdPartyPayment({
                    paymentToken,
                    internalId: data.internalId,
                    rentPaymentId: data.rentPaymentId,
                    subscriptionPaymentId: data.subscriptionPaymentId,
                });
                return;
            }
            if (provider === "stripe" && ev) {
                this.stripeThirdPartyPayment(ev, method);
            }
        },
        async handleSubscriptionSuccess() {
            this.isShowPreparing = true;
            try {
                await this.refreshAfterSubscriptionPayment();
            } catch (error) {
                this.toastPaymentError(error);
            } finally {
                this.isShowPreparing = false;
            }
        },
        triggerShake() {
            this.showShakeEffect = true;
            setTimeout(() => {
                this.showShakeEffect = false;
            }, 500);
        },
        handleGuestRequired() {
            this.isShowGuestPopup = true;
        },
        goLogin() {
            this.isShowGuestPopup = false;
            this.$store.dispatch("setToken", "");
            this.$router.push({ path: "/login" });
        },
    },
};
</script>
<style lang="scss">
@use "@/styles/variables.scss" as *;

.payment-select {
    display: flex;
    flex-direction: column;
    background: #f6f6f6;
    color: $text-color;
    padding-bottom: 110px;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
}

.privacy-agreement {
    display: flex;
    align-items: flex-start;
    width: 295px;
    margin: 0 auto 10px;
}

.privacy-agreement-spacer {
    flex: 1 1 auto;
    min-height: 10px;
}

.privacy-checkbox {
    margin-right: 9px;
    margin-top: 3px;
}

.privacy-text {
    line-height: 17.28px;
    font-size: 12px;
    color: #333;

    a {
        color: #3183ff;
        text-decoration: underline;
    }
}

@keyframes shake {
    0% { transform: translateX(0px); }
    25% { transform: translateX(-10px); }
    50% { transform: translateX(10px); }
    75% { transform: translateX(-10px); }
    100% { transform: translateX(0px); }
}

.shake {
    animation: shake 0.5s ease;
}

.unpaid-popup {
    display: flex;
    flex-direction: column;
    align-items: center;
    .icon svg {
        width: 26px;
        height: 26px;
    }
    .subtitle {
        font-family: SF Pro Display;
        font-weight: 500;
        font-size: 18px;
        line-height: 25.2px;
        color: #202124;
        margin-bottom: 4px;
    }
    .content {
        font-size: 16px;
        line-height: 23.04px;
        color: #666666;
        text-align: center;
    }
    .primary-btn {
        width: 100%;
        margin-top: 20px;
    }
}

.guest-popup .subtitle {
    text-align: center;
}

.payment-popup {
    padding: 32px 16px 0;
    text-align: center;
    position: relative;

    .close-icon {
        position: absolute;
        top: 13px;
        right: 12px;
        width: 34px;
        height: 34px;
        padding: 5px;
    }
    .payment-popup-title {
        font-size: 20px;
        font-weight: 590;
        line-height: 23.87px;
    }
}
</style>
